import { UNIT_IDS } from '../constants/guideConstants';

export function buildStockSendDscItem(warehouseName) {
	const name = String(warehouseName || '').trim();
	return name
		? `Envío RESPEL desde stock ${name}`
		: 'Envío RESPEL desde stock';
}

/** Nombre de bodega desde ítem stock o fallback del formulario (bodega origen). */
export function resolveStockSendWarehouseName(items, fallbackName = '') {
	for (const item of items || []) {
		const mv = item?._module_vars;
		if (!mv?._is_warehouse_stock_send) continue;
		const name =
			mv.warehouse?.name || mv.warehouse_name || '';
		if (name) return String(name).trim();
	}
	return String(fallbackName || '').trim();
}

/**
 * La glosa de bodega es la misma para todos los slots; solo va en el último ítem stock.
 * @param {{ warehouseName?: string }} [options] - nombre de bodega origen del formulario si el API no popula `warehouse`
 */
export function applyStockSendDescriptionToLastItem(items, options = {}) {
	if (!Array.isArray(items) || items.length === 0) {
		return items;
	}

	const stockIndices = [];

	items.forEach((item, index) => {
		const mv = item?._module_vars;
		if (!mv?._is_warehouse_stock_send) return;
		stockIndices.push(index);
	});

	const warehouseName = resolveStockSendWarehouseName(
		items,
		options.warehouseName,
	);

	if (stockIndices.length === 0) {
		return items;
	}

	const lastIndex = stockIndices[stockIndices.length - 1];
	const description = buildStockSendDscItem(warehouseName);

	return items.map((item, index) => {
		const mv = item?._module_vars;
		if (!mv?._is_warehouse_stock_send) {
			return item;
		}
		return {
			...item,
			DscItem: index === lastIndex ? description : '',
		};
	});
}

export const transformWarehouseStockToItems = stockSlots => {
	if (!Array.isArray(stockSlots) || stockSlots.length === 0) {
		return [];
	}
	return stockSlots.map((slot, index) => ({
		NroLinDet: index + 1,
		NmbItem: slot.residue?.name || 'Residuo',
		DscItem: '',
		QtyItem: Number(slot.kilograms_to_send || slot.kilograms_total || 0),
		UnmdItem: 'KG',
		PrcItem: 0,
		MontoItem: 0,
		VlrCodigo: slot.residue?.code || '',
		TpoCodigo: '',
		_module_vars: {
			_is_warehouse_stock_send: true,
			warehouse_id: slot.warehouse_id,
			residue_id: slot.residue_id,
			warehouse: slot.warehouse,
			residue: slot.residue,
			warehouse_name:
				slot.warehouse?.name || slot.warehouse_name || '',
			kilograms_to_send: Number(slot.kilograms_to_send || 0),
			kilograms_total: Number(slot.kilograms_total || 0),
		},
	}));
};

/**
 * Convierte guide_details del legacy a items del nuevo generador
 * AGRUPACIÓN POR ESTABLECIMIENTO DE ORIGEN:
 * - Cada establecimiento = 1 línea/item en el DTE
 * - NmbItem: "RESIDUOS INDUSTRIALES"
 * - DscItem: "DE GUIA nro1,nro2 DESCRIPCION1,DESCRIPCION2 NOMBRE_ESTABLECIMIENTO"
 * - QtyItem: Suma de kilogramos de todas las guías del establecimiento
 * - _module_vars: Arrays de guide_detail_ids y original_guide_ids para consolidación
 */
export const transformLegacyGuideDetailsToItems = (
	guideDetails,
	establishmentData,
) => {
	if (!Array.isArray(guideDetails) || guideDetails.length === 0) {
		return [];
	}

	// PASO 1: Agrupar por establecimiento de origen
	const groupedByEstablishment = {};

	guideDetails.forEach(detail => {
		const establishment = detail.guide?.establishment;
		const establishmentId = establishment?._id || 'unknown';
		const establishmentName = establishment?.name || 'SIN ESTABLECIMIENTO';

		if (!groupedByEstablishment[establishmentId]) {
			groupedByEstablishment[establishmentId] = {
				establishment,
				establishmentName,
				guideDetails: [],
			};
		}

		groupedByEstablishment[establishmentId].guideDetails.push(detail);
	});

	// PASO 2: Transformar cada grupo en un item del DTE
	return Object.entries(groupedByEstablishment).map(
		([establishmentId, group], index) => {
			const { establishment, establishmentName, guideDetails: details } = group;

			// Extraer números de guías únicos
			const guideNumbers = [
				...new Set(
					details.map(d => d.guide?.guide_number).filter(Boolean),
				),
			];

			// Extraer descripciones de residuos
			const residueDescriptions = details.map(
				d => d.residue?.name || 'Residuo',
			);

			// Sumar kilogramos totales
			const totalKilograms = details.reduce(
				(sum, d) => sum + (d.kilograms_to_send || d.kilograms_total || 0),
				0,
			);

			// Arrays de IDs para consolidación
			const guideDetailIds = details.map(d => d._id);
			const originalGuideIds = details.map(d => d.guide?._id);
			const residueIds = details.map(d => d.residue?._id);
			const unitMeasurementIds = details.map(
				d => d.unit_measurement?._id || UNIT_IDS.KG,
			);

			// Guardar datos individuales para reconstrucción precisa en el workflow
			const individualKilograms = details.map(
				d => d.kilograms_to_send || d.kilograms_total || 0,
			);

			// Construir descripción en formato legacy:
			// "DE GUIA nro1,nro2 DESCRIPCION1,DESCRIPCION2 NOMBRE_ESTABLECIMIENTO"
			const guideNumbersText = guideNumbers.join(',');
			const descriptionsText = residueDescriptions.join(',');
			const description = `DE GUIA ${guideNumbersText} ${descriptionsText} ${establishmentName}`;

			return {
				// Campos DTE estándar
				NroLinDet: index + 1,
				NmbItem: 'RESIDUOS INDUSTRIALES',
				DscItem: description,
				QtyItem: totalKilograms,
				UnmdItem: 'KG',
				PrcItem: 0,
				MontoItem: 0,
				VlrCodigo: '', // Sin código SAP específico para agrupación
				TpoCodigo: '',

				// Metadata CRÍTICA para el workflow post-DTE
				_module_vars: {
					// Arrays de identificadores (para consolidación múltiple)
					guide_detail_ids: guideDetailIds,
					original_guide_ids: originalGuideIds,
					residue_ids: residueIds,
					unit_measurement_ids: unitMeasurementIds,

					// Datos individuales para distribución precisa
					individual_kilograms: individualKilograms,

					// Datos del establecimiento para referencia
					establishment_id: establishment?._id,
					establishment_name: establishmentName,

					// Suma total de kilogramos
					kilograms_total: totalKilograms,

					// Flag para indicar que es agrupación por establecimiento
					_is_warehouse_send_grouped: true,
					_requires_consolidation: true,
				},
			};
		},
	);
};

/**
 * Prepara datos de origen desde el establecimiento seleccionado
 */
export const prepareOrigenData = establishment => {
	if (!establishment) return null;

	return {
		establishment_id: establishment._id,
		center_origin: establishment.name,
		company_name: establishment.company?.name || '',
		company_rut: establishment.company?.rut || '',
		establishment_address: establishment.address || '',
		establishment_commune: establishment.commune?.name || '',
		company_business_activity: establishment.company?.business_activity || '',
		company_business_activity_code:
			establishment.company?.business_activity_code || '',
		siep_code_origin: establishment.code_siep || '',
	};
};

/**
 * Crea el objeto completo de pre-llenado para el nuevo generador
 */
export const createWarehouseSendPrefillData = (
	guideDetails,
	establishment,
	residueTypeId,
) => {
	const isStockSend = guideDetails?.some(item => item?._is_warehouse_stock_send);
	const items = isStockSend
		? applyStockSendDescriptionToLastItem(
				transformWarehouseStockToItems(guideDetails),
			)
		: transformLegacyGuideDetailsToItems(guideDetails, establishment);
	const origen = prepareOrigenData(establishment);

	// Detectar tipo de operación según residue_type_id
	const residuos_operation_type = detectResidueOperationType(residueTypeId);

	return {
		category: 'residuos',
		residuos_operation_type,
		residue_type_id: residueTypeId,
		origen,
		items,
		// Destino y transporte se dejan vacíos para que el usuario los complete
		destino: {},
		transporte: {},
	};
};

/**
 * Detecta el tipo de operación según el residue_type_id
 */
const detectResidueOperationType = residueTypeId => {
	const PELIGROSOS_ID = process.env.REACT_APP_RESIDUE_TYPE_PELIGROSOS;
	const NO_PELIGROSOS_ID = process.env.REACT_APP_RESIDUE_TYPE_NO_PELIGROSOS;

	if (residueTypeId === PELIGROSOS_ID) {
		return 'envio_bodega_peligrosos';
	} else if (residueTypeId === NO_PELIGROSOS_ID) {
		return 'envio_bodega_no_peligrosos';
	}

	return 'envio_bodega_peligrosos'; // Default
};
